import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FileType, RootState } from '../../../../types/common.types';
import { Comment, HashTag, Member } from '../../../../types/entity.types';

import { useApprovalApi } from '../../../../api/approval/useApprovalApi';
import { useCommentApi } from '../../../../api/comment/useCommentApi';
import { usePushNotificationApi } from '../../../../api/notification/push/usePushNotificationApi';
import { useReactionApi } from '../../../../api/reaction/useReactionApi';

import TribeComment from '../../../../components/screens/programme/tribe/tribe-comment/TribeComment';

type Props = {
  status: string;
  comment: Comment;
  hashtags?: HashTag[];
  showReplies: boolean;
  postExternalReference?: string;
  reply?: boolean;
  approval?: boolean;
  refreshPost?: (value: string) => void;
  hierarchy?: boolean;
  hierarchyComments?: Comment[];
};

const TribeCommentContainer = ({
  status,
  comment,
  hashtags = [],
  showReplies,
  postExternalReference,
  reply = false,
  approval = false,
  refreshPost,
  hierarchy = false,
  hierarchyComments
}: Props) => {
  const [files, setFiles] = useState<FileType[]>([]);
  const [liked, setLiked] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);

  const { reactions } = useSelector(({ tribe }: RootState) => tribe);
  const { members } = useSelector(({ member }: RootState) => member);
  const { user } = useSelector(({ system }: RootState) => system);

  const navigate = useNavigate();

  const { programmeExternalReference, memberExternalReference } = useParams();

  const { sendPushNotification } = usePushNotificationApi();

  const { viewComments } = useCommentApi();

  const { createReaction: createReactionRequest, deleteReaction: deleteReactionRequest } = useReactionApi();

  const {
    updateCommentStatus: updateCommentStatusRequest,
    evaluateSupportConversation: evaluateSupportConversationRequest
  } = useApprovalApi();

  const [searchParams] = useSearchParams();

  const hasSubProgrammes = searchParams.get('hasSubProgrammes') === 'true';

  useEffect(() => {
    if (showReplies && !approval) {
      if (hierarchyComments) return;

      viewComments(
        {
          postExternalReference: comment.externalReference!,
          page: 0,
          size: 999
        },
        (data) => setComments(data)
      );
    } else if (approval) {
      setComments(comment.comments ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comment, showReplies, approval]);

  useEffect(() => {
    setFiles((comment.linkedMediaFiles as FileType[]) ?? []);
  }, [comment]);

  useEffect(() => {
    const filteredReactions = reactions.filter(
      (reaction) => reaction.postExternalReference === comment.externalReference
    );

    setLiked(filteredReactions.some((reaction) => reaction.reaction === 'like'));
  }, [reactions, comment]);

  const createReaction = (reaction: string) => {
    createReactionRequest(
      {
        postExternalReference: comment.externalReference!,
        programmeExternalReference: programmeExternalReference!,
        reaction
      },
      () => {
        refreshPost?.(postExternalReference as string);

        if (reaction !== 'like' || user?.username === comment.userName || !comment.memberExternalReference) return;

        sendPushNotification({
          recipientExternalReference: comment.memberExternalReference,
          title: `${user?.username} liked your comment`,
          data: JSON.stringify({
            screen: 'tribe-post-hierarchy',
            navigator: 'programme',
            params: { externalReference: comment.externalReference },
            programmeExternalReference
          })
        });
      }
    );
  };

  const deleteReaction = (reaction: string) => {
    const { memberExternalReference } = members.find((member) => member.username === user?.username) as Member;

    deleteReactionRequest(
      {
        postExternalReference: comment.externalReference!,
        memberExternalReference: memberExternalReference!,
        reaction
      },
      () => refreshPost?.(postExternalReference as string)
    );
  };

  const updateCommentStatus = (status: string, onSuccess: () => void) => {
    updateCommentStatusRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        commentExternalReference: comment.externalReference!,
        status
      },
      onSuccess
    );
  };

  const evaluateSupportConversation = (systemUserExternalReference: string) => {
    evaluateSupportConversationRequest(
      {
        programmeExternalReference: programmeExternalReference!,
        systemUserExternalReference
      },
      () =>
        navigate(
          `/programmes/${status}/${programmeExternalReference}/support/${systemUserExternalReference}?hasSubProgrammes=${hasSubProgrammes}`
        )
    );
  };

  const navigateToProfile = (username: string) => {
    const member = members.find((member) => member.username === username);

    if (!member || memberExternalReference === member.memberExternalReference) return;

    navigate(
      `/programmes/${status}/${programmeExternalReference}/tribe/${member.memberExternalReference}?hasSubProgrammes=${hasSubProgrammes}`
    );
  };

  if (hierarchyComments) {
    return (
      <TribeComment
        status={status}
        comment={comment}
        files={files}
        liked={liked}
        comments={hierarchyComments}
        hashtags={hashtags}
        showReplies={showReplies}
        postExternalReference={postExternalReference}
        reply={reply}
        approval={approval}
        createReaction={createReaction}
        deleteReaction={deleteReaction}
        refreshPost={refreshPost}
        updateCommentStatus={updateCommentStatus}
        evaluateSupportConversation={evaluateSupportConversation}
        hierarchy={hierarchy}
        navigateToProfile={navigateToProfile}
      />
    );
  }

  return (
    <TribeComment
      status={status}
      comment={comment}
      files={files}
      liked={liked}
      comments={comments}
      hashtags={hashtags}
      showReplies={showReplies}
      postExternalReference={postExternalReference}
      reply={reply}
      approval={approval}
      createReaction={createReaction}
      deleteReaction={deleteReaction}
      refreshPost={refreshPost}
      updateCommentStatus={updateCommentStatus}
      evaluateSupportConversation={evaluateSupportConversation}
      hierarchy={hierarchy}
      navigateToProfile={navigateToProfile}
    />
  );
};

export default TribeCommentContainer;
